/**
 * A mixin to share methods used in Spot- and DecoderEntry components.
 *
 */

import filter from 'lodash.filter';

import {isTagEntry} from '@/mixins/identifiers.js';

export function alertReadOnly() {
  window.alert(this.$t('noWriteAccess'));
}

export function getTimingCSS(entry, clickable = true) {
  if (!entry.has_timing) {
    return {};
  }
  let now = new Date();
  let startDate = entry.startdate ? new Date(entry.startdate) : new Date();
  let stopDate = entry.stopdate ? new Date(entry.stopdate) : new Date();

  // Ignore time, we only compare the dates.
  now.setHours(0, 0, 0, 0);
  startDate.setHours(0, 0, 0, 0);
  stopDate.setHours(0, 0, 0, 0);

  let startdateOnly = entry.startdate !== null && entry.stopdate === null;
  let invalid = !startdateOnly && startDate > stopDate;
  let future = startDate > now;
  let past = stopDate < now;

  let css = {clickable: clickable};

  if (invalid) {
    css['grey--text'] = true;
    return css;
  }
  if (future) {
    css['warning--text'] = true;
    return css;
  }
  if (past) {
    css['error--text'] = true;
    return css;
  }
  css['success--text'] = true;
  return css;
}

export function isActiveEntry(entry) {
  if (!entry.has_timing) {
    return true;
  }

  // Ignore time, we only compare the dates.
  let now = new Date();
  now.setHours(0, 0, 0, 0);

  let isInFuture = false;
  if (entry.startdate) {
    let startDate = new Date(entry.startdate);
    startDate.setHours(0, 0, 0, 0);
    isInFuture = now < startDate;
  }

  let isInPast = false;
  if (entry.stopdate) {
    let stopDate = new Date(entry.stopdate);
    stopDate.setHours(0, 0, 0, 0);
    isInPast = stopDate < now;
  }

  const isActive = !(isInPast || isInFuture);
  return isActive;
}

export function isDeactivatedTagEntry(entry) {
  return isTagEntry(entry) && !entry.active;
}

export function isFutureEntry(entry) {
  if (!entry.has_timing) {
    return false;
  }
  let startDate = new Date(entry.startdate);
  let now = new Date();

  // Ignore time, we only compare the dates.
  startDate.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const isInFuture = now < startDate;
  return isInFuture;
}

/**
 * Return those entries that don't have a past or future timing.
 *
 * Args:
 *   entries (Array[Object]): Serialized objects that originally
 *     were one of: RadioSpotEntry, TVSpotEntry, TickerEntry
 */
export function getActiveEntries(entries) {
  return filter(entries, isActiveEntry);
}

export function getDeactivatedTagEntries(entries) {
  return filter(entries, isDeactivatedTagEntry);
}

export function getFutureEntries(entries) {
  return filter(entries, isFutureEntry);
}

export function getEntriesWithTiming(entries) {
  return filter(entries, 'has_timing');
}

export function getEntriesWithHotrotation(entries) {
  return filter(entries, 'hotrotation');
}

export function getEntriesWithOverlayData(entries) {
  return entries.filter((entry) => {
    return entry.overlay_data && Object.keys(entry.overlay_data).length > 0;
  });
}

export function allEntriesUseTiming(entries) {
  const filtered = getEntriesWithTiming(entries);
  return filtered.length === entries.length;
}

export function allEntriesUseHotrotation(entries) {
  const filtered = getEntriesWithHotrotation(entries);
  return filtered.length === entries.length;
}

export function allEntriesHaveOverlayData(entries) {
  const filtered = getEntriesWithOverlayData(entries);
  return filtered.length === entries.length;
}

/**
 * Remove multiple SpotEntry/TickerEntry objects in one go.
 *
 * Args:
 *   allowedEntries (Array[Object]): Optional list of entries
 *     that have passed an access filter: If specified, remove only
 *     entries that are in both lists. We pass both lists to get
 *     a reactive update (first list is rendered).
 *
 * Returns:
 *   Promise
 */
export function batchRemoveMultipleEntries(entries, allowedEntries = null) {
  this.showConfirmRemoveMultipleDialog = false;

  let allowedEntryIdents = null;
  const filterByAllowed = allowedEntries !== null;
  if (filterByAllowed) {
    allowedEntryIdents = allowedEntries.map((entry) => entry.ident);
  }

  const promises = [];
  const entriesToDelete = [];

  for (let entry of entries) {
    if (filterByAllowed) {
      const contained = allowedEntryIdents.indexOf(entry.ident) !== -1;
      if (!contained) {
        continue;
      }
    }

    entriesToDelete.push(entry);
  }

  if (entriesToDelete.length) {
    const entryIdentsToDelete = entriesToDelete.map((entry) => entry.ident);
    const promise = this.$api.deleteSpotEntries(entryIdentsToDelete);
    promises.push(promise);
  }

  const allPromise = Promise.all(promises);
  allPromise
    .then((responses) => {
      this.$emit('deleted');
      this.$bus.$emit('alert-success', {
        message: 'allEntriesDeletedSuccess',
        timeout: 2000,
        translate: true,
      });
      this.$bus.$emit('invalidate-contents-cache');
    })
    .catch((error) => {
      this.$bus.$emit('alert-error', {
        message: 'genericFailFeedback',
        translate: true,
      });
    });
  return allPromise;
}

var commonEntry = {
  data() {
    return {
      entryToRemove: null,
      showConfirmRemovalDialog: false,
      showConfirmRemoveMultipleDialog: false,
    };
  },
  methods: {
    alertReadOnly: alertReadOnly,
    allEntriesHaveOverlayData: allEntriesHaveOverlayData,
    allEntriesUseHotrotation: allEntriesUseHotrotation,
    allEntriesUseTiming: allEntriesUseTiming,
    batchRemoveMultipleEntries: batchRemoveMultipleEntries,
    getActiveEntries: getActiveEntries,
    getDeactivatedTagEntries: getDeactivatedTagEntries,
    getEntriesWithHotrotation: getEntriesWithHotrotation,
    getEntriesWithTiming: getEntriesWithTiming,
    getFutureEntries: getFutureEntries,
    getTimingCSS: getTimingCSS,
    isActiveEntry: isActiveEntry,
    isDeactivatedTagEntry: isDeactivatedTagEntry,
    isFutureEntry: isFutureEntry,
    toggleEntryHotRotation(entry) {
      const newHotrotationValue = !entry.hotrotation;
      this.$api
        .batchUpdateEntries([entry.ident], {hotrotation: newHotrotationValue})
        .then((response) => {
          // FIXME: Remove vuex warning by using mutation.
          //
          // Slightly delayed to avoid menu animation glitch.
          setTimeout(() => {
            entry.hotrotation = newHotrotationValue;
          }, 250);

          this.$bus.$emit('alert-success', {
            message: newHotrotationValue ? 'hotrotationOn' : 'hotrotationOff',
            timeout: 2000,
            translate: true,
          });
        })
        .catch((error) => {
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
    },
    /**
     * Toggles Entries to either all use hotrotation or none.
     *
     * Args:
     *   value (Boolean) Optional. Use this value if given,
     *     otherwise new value will be true unless all entries already
     *     use hotrotation, then it will be false.
     */
    batchToggleEntriesHotRotation(entries, value = undefined) {
      if (!entries.length) {
        return;
      }
      if (value === undefined) {
        value = !this.allEntriesUseHotrotation(entries);
      }
      const entryIdents = entries.map((entry) => entry.ident);
      const payload = {
        hotrotation: value,
      };

      const promise = this.$api.batchUpdateEntries(entryIdents, payload);
      promise
        .then((response) => {
          // Update frontend.
          entries.map((entry) => (entry.hotrotation = value));

          this.$bus.$emit('alert-success', {
            message: value ? 'hotrotationsOn' : 'hotrotationsOff',
            translate: true,
          });
        })
        .catch((error) => {
          vm.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        });
      return promise;
    },
    removeEntry(ctype, entry) {
      this.showConfirmRemovalDialog = false;
      this.entryToRemove = null;

      this.$nextTick(() => {
        this.$api
          .deleteSpotEntry(ctype, entry.id)
          .then((response) => {
            this.$emit('deleted');
            this.$bus.$emit('alert-success', {
              message: 'entryDeletedSuccess',
              timeout: 2000,
              translate: true,
            });
            this.$bus.$emit('invalidate-contents-cache');
          })
          .catch((error) => {
            this.$bus.$emit('alert-error', {
              message: 'genericFailFeedback',
              translate: true,
            });
          });
      });
    },
    toggleActivateTagEntry(ctype, entry) {
      this.$nextTick(() => {
        this.$api
          .toggleActivateTagEntry(ctype, entry.id)
          .then((response) => {
            this.$emit('changed');
            this.$bus.$emit('invalidate-contents-cache');
          })
          .catch((error) => {
            this.$bus.$emit('alert-error', {
              message: 'genericFailFeedback',
              translate: true,
            });
          });
      });
    },
  },
};

export default commonEntry;



// WEBPACK FOOTER //
// ./src/mixins/commonEntry.js